export interface ProfileSubtitleSource {
  readonly title?: { readonly value: string } | null;
  readonly bio?: { readonly value: string } | null;
  readonly companyName: string | null;
}

export interface ProfileSubtitlePlainSource {
  readonly title: string | null;
  readonly bio?: string | null;
  readonly companyName: string | null;
}

type RenderWithCompany = (params: {
  readonly position: string;
  readonly company: string;
}) => string;

type ProfileSubtitleInput =
  | ProfileSubtitleSource
  | ProfileSubtitlePlainSource
  | null;

/**
 * Compose a participant's profile subtitle.
 *
 * Position is resolved from `title` or, as a fallback, `bio`. When both a
 * position and a company are present the caller-provided `renderWithCompany`
 * callback builds the localized combined string (typically via an i18n key
 * such as `profile.subtitle.withCompany`); otherwise, whichever single field
 * is non-empty is returned as-is.
 */
export function formatProfileSubtitle(
  source: ProfileSubtitlePlainSource | null,
  renderWithCompany: RenderWithCompany,
): string;
export function formatProfileSubtitle(
  source: ProfileSubtitleSource | null,
  renderWithCompany: RenderWithCompany,
): string;
export function formatProfileSubtitle(
  source: ProfileSubtitleInput,
  renderWithCompany: RenderWithCompany,
): string {
  if (source === null) return "";

  const rawTitle = source.title;
  const titleValue =
    typeof rawTitle === "string" ? rawTitle : (rawTitle?.value ?? undefined);
  const rawBio = source.bio;
  const bioValue =
    typeof rawBio === "string" ? rawBio : (rawBio?.value ?? undefined);

  const position =
    titleValue !== undefined && titleValue !== ""
      ? titleValue
      : bioValue !== undefined && bioValue !== ""
        ? bioValue
        : null;

  const companyValue = source.companyName;
  const company =
    companyValue !== null && companyValue !== "" ? companyValue : null;

  if (position !== null && company !== null) {
    return renderWithCompany({ position, company });
  }
  return position ?? company ?? "";
}
